import { History, RotateCcw } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { AgentDetail } from "@/api/generated/AgentDetail"
import type { AgentRevision } from "@/api/generated/AgentRevision"
import { useAgentRevisions, useRestoreRevision } from "@/api/queries/agents"
import { useConfirm } from "@/components/app/confirm-dialog"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState, ErrorState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { revisionChanges } from "@/features/studio/diff"
import { PromptDiff } from "@/features/studio/prompt-diff"
import { formatDateTime } from "@/lib/format"
import { cn } from "@/lib/utils"

/** The saved agent as a revision-shaped record (for "compare with current"). */
export function agentAsRevision(a: AgentDetail): AgentRevision {
  return {
    id: 0,
    model_id: a.model_id,
    model_name: a.model_name,
    system_prompt: a.system_prompt,
    temperature: a.temperature,
    top_p: a.top_p,
    top_k: a.top_k,
    snapshot: {
      max_tokens: a.max_tokens,
      thinking_enabled: a.thinking_enabled,
      reasoning_effort: a.reasoning_effort,
      max_steps: a.max_steps,
      description: a.description,
    },
    changed_by: null,
    changed_by_name: null,
    note: "",
    is_current: true,
    changed_at: a.updated_at,
  }
}

type Mode = "previous" | "current"

export function RevisionsTab({
  agent,
  dirty,
  onRestored,
}: {
  agent: AgentDetail
  dirty: boolean
  onRestored: (a: AgentDetail) => void
}) {
  const { t } = useTranslation()
  const revisions = useAgentRevisions(agent.id)
  const restore = useRestoreRevision(agent.id)
  const confirm = useConfirm()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [mode, setMode] = useState<Mode>("previous")

  const list = revisions.data ?? []
  const selected = list.find((r) => r.id === selectedId) ?? list[0]
  const index = selected ? list.indexOf(selected) : -1
  const previous = index >= 0 ? list[index + 1] : undefined
  const current = useMemo(() => agentAsRevision(agent), [agent])
  const base = mode === "current" ? selected : previous
  const target = mode === "current" ? current : selected
  const changes = base && target ? revisionChanges(base, target) : []

  const fieldLabel = (f: string) =>
    t(`studio.agents.revisions.fields.${f}` as "studio.agents.revisions.fields.model", f)

  const doRestore = (r: AgentRevision) =>
    confirm({
      title: t("studio.agents.revisions.restoreTitle"),
      description: [
        t("studio.agents.revisions.restoreDescription", { date: formatDateTime(r.changed_at, "PPp") }),
        dirty ? t("studio.agents.revisions.restoreDiscards") : "",
      ]
        .filter(Boolean)
        .join(" "),
      confirmLabel: t("studio.agents.revisions.restore"),
      onConfirm: async () => {
        const a = await restore.mutateAsync(r)
        onRestored(a)
        setSelectedId(null)
      },
    })

  if (revisions.isPending)
    return (
      <div className="grid max-w-6xl gap-4 @3xl:grid-cols-[18rem_minmax(0,1fr)]">
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
        <Skeleton className="h-72" />
      </div>
    )
  if (revisions.isError)
    return <ErrorState error={revisions.error} onRetry={() => void revisions.refetch()} />
  if (list.length === 0)
    return (
      <EmptyState
        icon={History}
        title={t("studio.agents.revisions.empty.title")}
        description={t("studio.agents.revisions.empty.description")}
      />
    )

  return (
    <div className="grid max-w-6xl items-start gap-4 @3xl:grid-cols-[18rem_minmax(0,1fr)]">
      <div className="space-y-2">
        <p className="text-muted-foreground text-xs">{t("studio.agents.revisions.hint")}</p>
        <ol className="relative space-y-1 before:absolute before:top-4 before:bottom-4 before:left-[11px] before:w-px before:bg-border">
          {list.map((r) => {
            const active = r.id === selected?.id
            return (
              <li key={r.id} className="relative">
                <button
                  type="button"
                  onClick={() => setSelectedId(r.id)}
                  aria-current={active || undefined}
                  className={cn(
                    "hover:bg-muted/60 focus-visible:ring-ring/50 flex w-full items-start gap-3 rounded-lg px-1.5 py-2 text-left outline-none focus-visible:ring-2",
                    active && "bg-muted",
                  )}
                >
                  <span
                    className={cn(
                      "bg-background relative z-10 mt-1 size-2.5 shrink-0 rounded-full border-2 ml-[5px]",
                      r.is_current ? "border-primary bg-primary" : "border-muted-foreground/40",
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">
                        {r.note || t("studio.agents.revisions.untitled", { id: r.id })}
                      </span>
                      {r.is_current && (
                        <StatusBadge tone="accent">{t("studio.agents.revisions.current")}</StatusBadge>
                      )}
                    </span>
                    <span className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 text-xs">
                      <RelativeTime value={r.changed_at} />
                      {r.changed_by_name && <span>· {r.changed_by_name}</span>}
                      {r.model_name && <span className="truncate">· {r.model_name}</span>}
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
        </ol>
      </div>

      {selected && (
        <div className="bg-card min-w-0 space-y-4 rounded-xl border p-4 sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-semibold">
                {selected.note || t("studio.agents.revisions.untitled", { id: selected.id })}
              </h3>
              <p className="text-muted-foreground text-xs">
                {t("studio.agents.revisions.savedBy", {
                  date: formatDateTime(selected.changed_at, "PPp"),
                  user: selected.changed_by_name ?? t("studio.agents.revisions.system"),
                })}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ToggleGroup
                variant="outline"
                size="sm"
                spacing={0}
                value={[mode]}
                onValueChange={(v) => {
                  const m = (v as Mode[])[0]
                  if (m) setMode(m)
                }}
                aria-label={t("studio.agents.revisions.compareWith")}
              >
                <ToggleGroupItem value="previous" className="px-2.5 text-xs">
                  {t("studio.agents.revisions.vsPrevious")}
                </ToggleGroupItem>
                <ToggleGroupItem value="current" className="px-2.5 text-xs">
                  {t("studio.agents.revisions.vsCurrent")}
                </ToggleGroupItem>
              </ToggleGroup>
              {!selected.is_current && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void doRestore(selected)}
                  disabled={restore.isPending}
                  data-testid="restore-revision"
                >
                  <RotateCcw />
                  {t("studio.agents.revisions.restore")}
                </Button>
              )}
            </div>
          </div>

          {mode === "previous" && !previous ? (
            <div className="space-y-2">
              <p className="text-muted-foreground text-sm">{t("studio.agents.revisions.first")}</p>
              <PromptDiff before="" after={selected.system_prompt} />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <h4 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  {t("studio.agents.revisions.settings")}
                </h4>
                {changes.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    {t("studio.agents.revisions.noSettingChanges")}
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-lg border">
                    <table className="w-full table-fixed text-sm">
                      <thead className="bg-muted/40 text-muted-foreground text-xs">
                        <tr>
                          <th className="w-1/3 px-3 py-2 text-left font-medium">{t("studio.diff.field")}</th>
                          <th className="px-3 py-2 text-left font-medium">{t("studio.diff.before")}</th>
                          <th className="px-3 py-2 text-left font-medium">{t("studio.diff.after")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {changes.map((c) => (
                          <tr key={c.field} className="align-top">
                            <td className="px-3 py-2 text-[0.8125rem]">{fieldLabel(c.field)}</td>
                            <td className="bg-destructive/[0.04] decoration-destructive/40 px-3 py-2 text-[0.8125rem] break-words line-through">
                              {c.before}
                            </td>
                            <td className="bg-success/[0.05] px-3 py-2 text-[0.8125rem] break-words">
                              {c.after}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <h4 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  {t("studio.agents.revisions.prompt")}
                </h4>
                {base && target && <PromptDiff before={base.system_prompt} after={target.system_prompt} />}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
